import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { InvoiceDisputeBadge, InvoiceStatusBadge } from '@/components/invoices/InvoiceStatusBadge';
import { Invoice } from '@/types/invoice';
import { ExternalLink, Eye } from 'lucide-react';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { InvoiceQuickViewSheet } from '@/components/invoices/InvoiceQuickViewSheet';

interface CustomerInvoicesTableProps {
  invoices: Invoice[];
  isLoading?: boolean;
}

export function CustomerInvoicesTable({ invoices, isLoading }: CustomerInvoicesTableProps) {
  const navigate = useNavigate();
  // Open the invoice here rather than jumping to its job or the invoice list.
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

  const { formatMoney: formatCurrency, formatShortDate: formatDate } = useCompanyLocale();

  if (isLoading) {
    return (
      <div className="border rounded-lg p-8 text-center">
        <p className="text-muted-foreground">Loading invoices...</p>
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center">
        <p className="text-muted-foreground">No invoices for this customer yet.</p>
        <Button 
          variant="outline" 
          className="mt-4"
          onClick={() => navigate('/invoices')}
        >
          Go to Invoices
        </Button>
      </div>
    );
  }

  const totalOutstanding = invoices.reduce((sum, inv) => sum + inv.amount_outstanding, 0);
  const totalPaid = invoices.reduce((sum, inv) => sum + inv.amount_paid, 0);

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-muted/50 p-4 rounded-lg">
          <p className="text-sm text-muted-foreground">Total Invoices</p>
          <p className="text-2xl font-bold">{invoices.length}</p>
        </div>
        <div className="bg-success/10 p-4 rounded-lg">
          <p className="text-sm text-muted-foreground">Total Paid</p>
          <p className="text-2xl font-bold text-success">{formatCurrency(totalPaid)}</p>
        </div>
        <div className="bg-warning/10 p-4 rounded-lg">
          <p className="text-sm text-muted-foreground">Outstanding</p>
          <p className="text-2xl font-bold text-warning">{formatCurrency(totalOutstanding)}</p>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice #</TableHead>
              <TableHead>Issue Date</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[80px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((invoice) => (
              <TableRow
                key={invoice.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => setSelectedInvoiceId(invoice.id)}
              >
                <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                <TableCell>{formatDate(invoice.issue_date)}</TableCell>
                <TableCell>{formatDate(invoice.due_date)}</TableCell>
                <TableCell className="text-right font-medium">
                  {formatCurrency(invoice.total_amount)}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <InvoiceStatusBadge status={invoice.status} />
                    <InvoiceDisputeBadge state={invoice.dispute_state} />
                  </div>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedInvoiceId(invoice.id)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <InvoiceQuickViewSheet
        invoiceId={selectedInvoiceId}
        open={!!selectedInvoiceId}
        onOpenChange={(next) => !next && setSelectedInvoiceId(null)}
      />
    </div>
  );
}
